import React, { useMemo, useState } from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors } from './Theme/WebTheme';
import { FONT_FAMILY } from '../common/sharedFunctions';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const FAQSection = React.memo(() => {
  const { i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const langCode = useMemo(() => 
    (i18n?.language ? i18n.language.slice(0, 2).toLowerCase() : 'fr'),
    [i18n?.language]
  );

  const faqContent = useMemo(() => ({
    fr: {
      title: 'Questions Fréquentes',
      subtitle: 'Tout ce que vous devez savoir sur notre application et nos services de transport éco-responsable',
      faqs: [
        {
          question: 'Comment fonctionne l\'application Allô Driver ?',
          answer: 'Téléchargez notre application mobile sur iOS et Android, indiquez votre départ et votre destination, consultez l’estimation avant validation, puis suivez votre chauffeur lorsque la course est confirmée.'
        },
        {
          question: 'Pourquoi vos tarifs sont-ils plus avantageux ?',
          answer: 'Le tarif est présenté avant validation selon le trajet, le véhicule et les paramètres disponibles dans l’application. L’objectif est de donner une information claire avant la réservation.'
        },
        {
          question: 'Qu\'est-ce qui rend votre flotte éco-responsable ?',
          answer: 'Nous privilégions les véhicules hybrides et électriques lorsque cela est possible. La disponibilité dépend de la flotte active, du secteur et du chauffeur assigné.'
        },
        {
          question: 'Puis-je suivre ma course en temps réel sur l\'application ?',
          answer: 'L’application permet de suivre les informations de course disponibles : chauffeur assigné, position lorsqu’elle est transmise, heure estimée et contact chauffeur.'
        },
        {
          question: 'Comment sont calculés les prix dans l\'application ?',
          answer: 'Le prix est estimé à partir du départ, de la destination, du type de véhicule et des paramètres de tarification. Le montant affiché avant validation sert de base à la réservation.'
        },
        {
          question: 'Quels véhicules hybrides et électriques proposez-vous ?',
          answer: 'La flotte peut inclure des véhicules hybrides, électriques et des vans selon les disponibilités locales. Les équipements varient selon le véhicule assigné.'
        },
        {
          question: 'L\'application permet-elle de réserver à l\'avance ?',
          answer: 'Oui, vous pouvez planifier certains trajets à l’avance depuis l’application. La confirmation dépend de la disponibilité chauffeur.'
        },
        {
          question: 'Puis-je payer directement via l\'application ?',
          answer: 'Le paiement dans l’application dépend des moyens activés : carte bancaire, Apple Pay, Google Pay ou wallet selon configuration. Les factures sont disponibles lorsque la course est finalisée.'
        }
      ]
    },
    en: {
      title: 'Frequently Asked Questions',
      subtitle: 'Everything you need to know about our app and eco-responsible transportation services',
      faqs: [
        {
          question: 'How does the Allô Driver app work?',
          answer: 'Download the mobile app on iOS or Android, enter pickup and destination, review the fare estimate before confirmation, then track the ride once a driver is assigned.'
        },
        {
          question: 'Why are your rates more competitive?',
          answer: 'The app displays a fare estimate before confirmation based on the ride, vehicle type and available pricing settings. The goal is to provide clear information before booking.'
        },
        {
          question: 'What makes your fleet eco-responsible?',
          answer: 'We prioritize hybrid and electric vehicles when available. Availability depends on the active fleet, area and assigned driver.'
        },
        {
          question: 'Can I track my ride in real-time on the app?',
          answer: 'The app shows available ride information: assigned driver, location when transmitted, estimated time and driver contact details.'
        },
        {
          question: 'How are prices calculated in the app?',
          answer: 'The fare is estimated from pickup, destination, vehicle type and pricing settings. The amount shown before confirmation is used as the booking basis.'
        },
        {
          question: 'What hybrid and electric vehicles do you offer?',
          answer: 'The fleet may include hybrid vehicles, electric vehicles and vans depending on local availability. Equipment varies depending on the assigned vehicle.'
        },
        {
          question: 'Does the app allow advance booking?',
          answer: 'Yes, some rides can be scheduled in advance from the app. Confirmation depends on driver availability.'
        },
        {
          question: 'Can I pay directly through the app?',
          answer: 'In-app payment depends on enabled methods: card, Apple Pay, Google Pay or wallet depending on configuration. Invoices are available when the ride is finalized.'
        }
      ]
    }
  }), []);

  const content = useMemo(() => 
    faqContent[langCode] || faqContent.fr,
    [faqContent, langCode]
  );

  return (
    <Box sx={{ 
      py: { xs: 6, md: 10 }, 
      px: { xs: 2, md: 4 },
      backgroundColor: 'transparent'
    }}>
      <Box sx={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontFamily: FONT_FAMILY,
              fontWeight: 700,
              fontSize: { xs: '32px', md: '48px' },
              color: '#fff',
              mb: 2
            }}
          >
            {content.title}
          </Typography>
          <Typography 
            sx={{ 
              fontFamily: FONT_FAMILY,
              fontSize: { xs: '16px', md: '18px' },
              color: 'rgba(255,255,255,0.7)',
              lineHeight: 1.6
            }}
          >
            {content.subtitle}
          </Typography>
        </Box>

        {/* FAQ Grid */}
        <Grid container spacing={3}>
          {content.faqs.map((faq, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Accordion
                expanded={expanded === `panel${index}`}
                onChange={handleChange(`panel${index}`)}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  borderRadius: '12px !important',
                  height: '100%',
                  '&:before': {
                    display: 'none'
                  },
                  '&.Mui-expanded': {
                    margin: '0 !important',
                    borderColor: colors.Button_Primary,
                    boxShadow: `0 8px 32px rgba(212, 175, 55, 0.2)`
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: colors.Button_Primary, fontSize: 28 }} />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      margin: '16px 0'
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(212, 175, 55, 0.05)'
                    }
                  }}
                >
                  <Typography 
                    sx={{ 
                      fontFamily: FONT_FAMILY,
                      fontWeight: 700,
                      fontSize: { xs: '15px', md: '17px' },
                      color: '#fff',
                      lineHeight: 1.4
                    }}
                  >
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 3 }}>
                  <Typography 
                    sx={{ 
                      fontFamily: FONT_FAMILY,
                      fontSize: { xs: '13px', md: '15px' },
                      color: 'rgba(255,255,255,0.8)',
                      lineHeight: 1.7
                    }}
                  >
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
});

export default FAQSection;
